/**
 * CommitmentTracker — Track promises made and received.
 *
 * Displays commitments with:
 * - Type (made_by_user vs made_by_contact)
 * - Due date and overdue status
 * - Complete/cancel actions
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Target, ChevronDown, ChevronRight, Loader2, AlertCircle,
  Check, Clock, ArrowRight, ArrowLeft, Plus,
} from 'lucide-react';
import apiClient from '../../lib/apiClient';
import './CommitmentTracker.css';

function formatDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

function daysUntil(iso) {
  if (!iso) return null;
  const diff = Math.ceil((new Date(iso) - Date.now()) / 86400000);
  return diff;
}

function CommitmentTracker({ accountId, collapsed = false }) {
  const queryClient = useQueryClient();
  const [isExpanded, setIsExpanded] = useState(!collapsed);
  const [filter, setFilter] = useState('pending');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCommitment, setNewCommitment] = useState({
    commitment_type: 'made_by_user',
    description: '',
    due_date: '',
    priority: 50,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['account-intelligence', 'commitments', filter],
    queryFn: () => apiClient.get(`/api/account-intelligence/commitments?filter=${filter}`),
    staleTime: 60_000,
  });

  const completeMutation = useMutation({
    mutationFn: ({ commitmentId, notes }) =>
      apiClient.post(`/api/account-intelligence/commitments/${commitmentId}/complete`, { notes }),
    onMutate: async ({ commitmentId }) => {
      // Optimistic — mark completed immediately so the card doesn't hang.
      const key = ['account-intelligence', 'commitments', filter];
      const previous = queryClient.getQueryData(key);
      if (previous) {
        queryClient.setQueryData(key, {
          ...previous,
          commitments: (previous.commitments || []).map((c) =>
            c.id === commitmentId ? { ...c, status: 'completed' } : c
          ),
        });
      }
      return { key, previous };
    },
    onError: (_err, _vars, ctx) => {
      // Roll back optimistic update on failure.
      if (ctx?.previous) queryClient.setQueryData(ctx.key, ctx.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['account-intelligence', 'commitments'] });
    },
  });

  const createMutation = useMutation({
    mutationFn: (commitment) =>
      apiClient.post('/api/account-intelligence/commitments', commitment),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-intelligence', 'commitments'] });
      setShowAddForm(false);
      setNewCommitment({
        commitment_type: 'made_by_user',
        description: '',
        due_date: '',
        priority: 50,
      });
    },
  });

  const commitments = data?.commitments || [];
  const accountCommitments = accountId
    ? commitments.filter((c) => c.account_id === accountId)
    : commitments;

  const handleCreate = (e) => {
    e.preventDefault();
    if (!newCommitment.description.trim()) return;
    createMutation.mutate({
      ...newCommitment,
      account_id: accountId,
    });
  };

  const pendingCount = commitments.filter((c) => c.status === 'pending').length;
  const overdueCount = commitments.filter((c) => c.status === 'overdue').length;

  return (
    <div className="ct-container">
      <button
        className="ct-header"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
      >
        <div className="ct-header-left">
          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <Target size={16} />
          <span className="ct-title">Commitments</span>
          {pendingCount > 0 && (
            <span className="ct-badge">{pendingCount}</span>
          )}
          {overdueCount > 0 && (
            <span className="ct-badge ct-badge-overdue">{overdueCount}</span>
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="ct-body">
          <div className="ct-filters">
            {['pending', 'overdue', 'completed', 'all'].map((f) => (
              <button
                key={f}
                className={`ct-filter ${filter === f ? 'ct-filter-active' : ''}`}
                onClick={() => setFilter(f)}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
            <button
              className="ct-add-btn"
              onClick={() => setShowAddForm(!showAddForm)}
            >
              <Plus size={14} />
            </button>
          </div>

          {showAddForm && (
            <form className="ct-add-form" onSubmit={handleCreate}>
              <div className="ct-form-row">
                <select
                  value={newCommitment.commitment_type}
                  onChange={(e) =>
                    setNewCommitment((c) => ({ ...c, commitment_type: e.target.value }))
                  }
                >
                  <option value="made_by_user">I promised</option>
                  <option value="made_by_contact">They promised</option>
                </select>
                <input
                  type="date"
                  value={newCommitment.due_date}
                  onChange={(e) =>
                    setNewCommitment((c) => ({ ...c, due_date: e.target.value }))
                  }
                  placeholder="Due date"
                />
              </div>
              <textarea
                value={newCommitment.description}
                onChange={(e) =>
                  setNewCommitment((c) => ({ ...c, description: e.target.value }))
                }
                placeholder="What was committed?"
                rows={2}
              />
              <div className="ct-form-actions">
                <button
                  type="button"
                  className="ct-btn-cancel"
                  onClick={() => setShowAddForm(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="ct-btn-submit"
                  disabled={!newCommitment.description.trim() || createMutation.isPending}
                >
                  {createMutation.isPending ? (
                    <Loader2 size={12} className="ct-spinner" />
                  ) : (
                    <Plus size={12} />
                  )}
                  Add
                </button>
              </div>
            </form>
          )}

          {isLoading ? (
            <div className="ct-loading">
              <Loader2 size={16} className="ct-spinner" />
              <span>Loading commitments...</span>
            </div>
          ) : error ? (
            <div className="ct-error">
              <AlertCircle size={14} />
              <span>Failed to load commitments</span>
            </div>
          ) : accountCommitments.length === 0 ? (
            <div className="ct-empty">
              <Check size={16} />
              <span>
                {filter === 'pending'
                  ? 'No pending commitments'
                  : filter === 'overdue'
                  ? 'No overdue commitments'
                  : 'No commitments yet'}
              </span>
            </div>
          ) : (
            <div className="ct-list">
              {accountCommitments.map((commitment) => {
                const days = daysUntil(commitment.due_date);
                const isOverdue = days !== null && days < 0;
                const isDueSoon = days !== null && days >= 0 && days <= 3;

                return (
                  <div
                    key={commitment.id}
                    className={`ct-card ${isOverdue ? 'ct-card-overdue' : ''} ${commitment.status === 'completed' ? 'ct-card-completed' : ''}`}
                  >
                    <div className="ct-card-header">
                      <div className="ct-direction">
                        {commitment.commitment_type === 'made_by_user' ? (
                          <>
                            <ArrowRight size={12} />
                            <span>You promised</span>
                          </>
                        ) : (
                          <>
                            <ArrowLeft size={12} />
                            <span>They promised</span>
                          </>
                        )}
                      </div>
                      {commitment.due_date && (
                        <div
                          className={`ct-due ${isOverdue ? 'ct-due-overdue' : ''} ${isDueSoon ? 'ct-due-soon' : ''}`}
                        >
                          <Clock size={10} />
                          {isOverdue
                            ? `${Math.abs(days)}d overdue`
                            : days === 0
                            ? 'Today'
                            : days === 1
                            ? 'Tomorrow'
                            : formatDate(commitment.due_date)}
                        </div>
                      )}
                    </div>

                    <p className="ct-description">{commitment.description}</p>

                    {commitment.status === 'pending' && (
                      <div className="ct-actions">
                        <button
                          className="ct-btn-complete"
                          onClick={() =>
                            completeMutation.mutate({ commitmentId: commitment.id, notes: '' })
                          }
                          disabled={
                            completeMutation.isPending &&
                            completeMutation.variables?.commitmentId === commitment.id
                          }
                        >
                          <Check size={12} />
                          Complete
                        </button>
                      </div>
                    )}

                    {commitment.status === 'completed' && (
                      <div className="ct-completed-badge">
                        <Check size={10} />
                        Completed
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default CommitmentTracker;
